import { useUserData } from '../contexts/UserDataContext';

// Format number with commas
function formatNumber(num: number, decimals: number = 2): string {
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function PositionsTab() {
  const { positions, isConnected } = useUserData();

  // Show empty state only if not connected or no positions (not during loading updates)
  const showEmptyState = !isConnected || positions.length === 0;
  const emptyMessage = !isConnected
    ? 'Connect wallet to view positions'
    : 'No open positions';

  return (
    <div className="h-full bg-[#0e0f10]">
      <table className="w-full">
        <thead>
          <tr className="border-b border-[#1a1d20]">
            <th className="px-3 py-1.5 h-6 text-left text-[12px] font-medium text-[#6c7884] tracking-wide">
              Asset
            </th>
            <th className="px-3 py-1.5 h-6 text-left text-[12px] font-medium text-[#6c7884] tracking-wide">
              Position
            </th>
            <th className="px-3 py-1.5 h-6 text-left text-[12px] font-medium text-[#6c7884] tracking-wide">
              Value
            </th>
            <th className="px-3 py-1.5 h-6 text-left text-[12px] font-medium text-[#6c7884] tracking-wide">
              Entry
            </th>
            <th className="px-3 py-1.5 h-6 text-left text-[12px] font-medium text-[#6c7884] tracking-wide">
              Mark
            </th>
            <th className="px-3 py-1.5 h-6 text-left text-[12px] font-medium text-[#6c7884] tracking-wide">
              PnL
            </th>
            <th className="px-3 py-1.5 h-6 text-left text-[12px] font-medium text-[#6c7884] tracking-wide">
              Liq.Price
            </th>
            <th className="px-3 py-1.5 h-6 text-left text-[12px] font-medium text-[#6c7884] tracking-wide">
              Margin
            </th>
            <th className="px-3 py-1.5 h-6 text-left text-[12px] font-medium text-[#6c7884] tracking-wide">
              Funding
            </th>
            <th className="px-3 py-1.5 h-6 text-center text-[12px] font-medium text-[#6c7884] tracking-wide">
              Close All
            </th>
            <th className="px-3 py-1.5 h-6 text-center text-[12px] font-medium text-[#6c7884] tracking-wide">
              TP/SL
            </th>
          </tr>
        </thead>
        <tbody>
          {showEmptyState ? (
            <tr>
              <td colSpan={11} className="px-3 py-12 text-center text-[#6c7884] text-[13px]">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            positions.map((position, idx) => {
            const size = parseFloat(position.szi);
            const pnl = parseFloat(position.unrealizedPnl);
            const isLong = size > 0;
            const entryPrice = parseFloat(position.entryPx);
            const margin = parseFloat(position.marginUsed);
            const leverage = position.leverage || 1;

            // Calculate mark price from unrealized PnL
            const markPrice = size !== 0 ? entryPrice + (pnl / size) : entryPrice;
            const positionValue = Math.abs(size) * markPrice;

            // Calculate PnL percentage (ROE)
            const pnlPercent = margin !== 0 ? (pnl / margin) * 100 : 0;

            // Get cumulative funding (total funding for the position)
            const funding = parseFloat(position.cumFunding || '0');

            return (
              <tr key={idx} className="hover:bg-[#13141a] transition-colors">
                {/* Asset - Left aligned, ticker + leverage (colored by direction) */}
                <td className="px-3 py-1.5 h-6 text-left text-white font-medium text-[12px]">
                  {position.coin}{' '}
                  <span className={isLong ? 'text-[#0ecb81]' : 'text-[#f6465d]'}>{leverage}x</span>
                </td>

                {/* Position - LEFT aligned, number + unit */}
                <td className="px-3 py-1.5 h-6 text-left text-[12px] text-white">
                  {formatNumber(Math.abs(size), 4)} {position.coin}
                </td>

                {/* Value - LEFT aligned, with USDC suffix */}
                <td className="px-3 py-1.5 h-6 text-left text-white text-[12px]">
                  {formatNumber(positionValue, 2)} USDC
                </td>

                {/* Entry - LEFT aligned, NO $ sign */}
                <td className="px-3 py-1.5 h-6 text-left text-white text-[12px]">
                  {formatNumber(entryPrice, entryPrice >= 100 ? 1 : 2)}
                </td>

                {/* Mark - LEFT aligned, NO $ sign */}
                <td className="px-3 py-1.5 h-6 text-left text-white text-[12px]">
                  {formatNumber(markPrice, markPrice >= 100 ? 1 : 2)}
                </td>

                {/* PnL - LEFT aligned, colored, with % in parens, NO $ sign */}
                <td className={`px-3 py-1.5 h-6 text-left text-[12px] font-medium ${
                  pnl >= 0 ? 'text-[#0ecb81]' : 'text-[#f6465d]'
                }`}>
                  {pnl >= 0 ? '+' : ''}{formatNumber(pnl, 2)} ({pnlPercent >= 0 ? '+' : ''}{formatNumber(pnlPercent, 1)}%)
                </td>

                {/* Liq.Price - LEFT aligned, WHITE */}
                <td className="px-3 py-1.5 h-6 text-left text-white text-[12px]">
                  {position.liquidationPx ? formatNumber(parseFloat(position.liquidationPx), parseFloat(position.liquidationPx) >= 100 ? 1 : 2) : 'N/A'}
                </td>

                {/* Margin - LEFT aligned, with (Cross) in WHITE (not colored) */}
                <td className="px-3 py-1.5 h-6 text-left text-white text-[12px]">
                  ${formatNumber(margin, 2)} (Cross)
                </td>

                {/* Funding - LEFT aligned, colored */}
                <td className={`px-3 py-1.5 h-6 text-left text-[12px] ${
                  funding >= 0 ? 'text-[#0ecb81]' : 'text-[#f6465d]'
                }`}>
                  {funding >= 0 ? '+' : ''}{formatNumber(funding, 2)}
                </td>

                {/* Close All - Center aligned, connection status */}
                <td className="px-3 py-1.5 h-6 text-center text-[#6c7884] text-[12px]">
                  Connected
                </td>

                {/* TP/SL - Center aligned, shows --/-- */}
                <td className="px-3 py-1.5 h-6 text-center text-[#6c7884] text-[12px]">
                  --/--
                </td>
              </tr>
            );
          })
          )}
        </tbody>
      </table>
    </div>
  );
}
